import { useTranslation } from 'react-i18next';
import type { EmployerGuideContext, GuideContext } from '@/shared/api/onboarding';
import { isCompletable, type GuideSectionDef } from './types';
import { stableId } from './progress';
import { resolveCtaHref } from './cta';
import { GuideStepCard } from './GuideStepCard';
import {
  CantonInfoCard,
  RatesCard,
  VavEligibilityCard,
  DeadlinesCard,
  DocumentList,
  type DocItem,
} from './ContextCards';
import styles from './SetupGuide.module.css';

interface Props {
  section: GuideSectionDef;
  ctx: GuideContext;
  guideRole: 'employer' | 'employee';
  completedSet: Set<string>;
  employerDocs: DocItem[];
  employeeDocs: DocItem[];
  /** Toggle a set of the section's manual steps to `done` in one go. */
  onToggleSteps: (stepStableIds: string[], done: boolean) => void;
}

/** Renders a single guide section: header with its completion control, an optional
 *  personalized block, and its step cards. */
export function GuideSectionView({
  section,
  ctx,
  guideRole,
  completedSet,
  employerDocs,
  employeeDocs,
  onToggleSteps,
}: Props) {
  const { t } = useTranslation();

  // Per-step completion, combining auto-derived facts with manual acknowledgements. The
  // completion control lives once in the section header so it sits in the same place for
  // every section, rather than at the foot of variable-height step cards.
  const perStep = section.steps.filter(isCompletable).map((step) => {
    const auto = Boolean(step.autoDeriveKey && ctx.autoDerived?.[step.autoDeriveKey]);
    const id = stableId(section.id, step);
    return { id, auto, manual: Boolean(step.manualCheck), done: auto || completedSet.has(id) };
  });
  const hasControl = perStep.length > 0;
  const sectionDone = hasControl && perStep.every((p) => p.done);
  // Manual steps the user can still tick (an auto-derived step is settled by the server).
  const pendingManual = perStep.filter((p) => p.manual && !p.auto);
  const fullyAuto = hasControl && pendingManual.length === 0;

  function handleToggle() {
    if (sectionDone) {
      const ids = perStep.filter((p) => p.manual && completedSet.has(p.id)).map((p) => p.id);
      if (ids.length) onToggleSteps(ids, false);
    } else {
      const ids = pendingManual.filter((p) => !p.done).map((p) => p.id);
      if (ids.length) onToggleSteps(ids, true);
    }
  }

  function renderControl() {
    if (!hasControl) return null;
    if (fullyAuto) {
      return sectionDone ? (
        <span className={styles.autoPill}>
          <i className="fa-solid fa-check" aria-hidden="true" />
          {t('guide.ui.done')}
        </span>
      ) : null;
    }
    return (
      <button
        type="button"
        className={`${styles.checkBtn} ${sectionDone ? styles.checkBtnDone : ''}`}
        onClick={handleToggle}
        aria-pressed={sectionDone}
      >
        <i
          className={`fa-${sectionDone ? 'solid' : 'regular'} fa-${sectionDone ? 'check' : 'circle'}`}
          aria-hidden="true"
        />
        {sectionDone ? t('guide.ui.marked') : t('guide.ui.markDone')}
      </button>
    );
  }

  function renderExtra() {
    if (!section.extra) return null;
    if (section.extra === 'documents') {
      return <DocumentList items={guideRole === 'employee' ? employeeDocs : employerDocs} />;
    }
    if (guideRole !== 'employer') return null;
    const emp = ctx as EmployerGuideContext;
    switch (section.extra) {
      case 'cantonInfo':
        return <CantonInfoCard ctx={emp} />;
      case 'rates':
        return <RatesCard ctx={emp} />;
      case 'vavEligibility':
        return <VavEligibilityCard ctx={emp} />;
      case 'deadlines':
        return <DeadlinesCard ctx={emp} />;
      default:
        return null;
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <div className={styles.sectionIcon}>
          <i className={`fa-solid ${section.icon}`} aria-hidden="true" />
        </div>
        <div className={styles.sectionHeadText}>
          <h3 className={styles.sectionTitle}>{t(section.titleKey)}</h3>
          {section.descKey && <p className={styles.sectionDesc}>{t(section.descKey)}</p>}
        </div>
        {renderControl()}
      </div>

      {renderExtra()}

      {section.steps.length > 0 && (
        <div className={styles.steps}>
          {section.steps.map((step) => {
            const auto = Boolean(step.autoDeriveKey && ctx.autoDerived?.[step.autoDeriveKey]);
            const done = auto || completedSet.has(stableId(section.id, step));
            return (
              <GuideStepCard
                key={step.id}
                step={step}
                done={done}
                ctaHref={resolveCtaHref(step, ctx)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
